<!-- Global, read-only Approval Request Details dialog. Opened from a notification click on
     any page (NotificationActionService) — no route change. -->
<p-dialog
  [(visible)]="visible"
  (onHide)="close()"
  [modal]="true"
  [draggable]="false"
  [resizable]="false"
  [dismissableMask]="false"
  [style]="{ width: '820px', maxHeight: '90vh' }"
  [appendTo]="'body'"
  [header]="dialogTitle"
  styleClass="detail-dialog"
>
  <!-- Loading -->
  <div class="detail-loading" *ngIf="loading">
    <i class="pi pi-spin pi-spinner"></i> Loading details…
  </div>

  <ng-container *ngIf="!loading && selectedReq">

    <!-- ── Header meta ──────────────────────────────────────────────────── -->
    <div class="detail-header">
      <div class="detail-title-row">
        <div>
          <h3 class="detail-title">{{ transactionLabel(selectedReq.transactionType) }}</h3>
          <p class="detail-sub">Request #{{ selectedReq.requestId }}</p>
        </div>
        <div class="detail-title-actions">
          <button
            *ngIf="canEdit"
            pButton type="button"
            class="p-button-outlined p-button-sm"
            icon="pi pi-pencil"
            label="Edit"
            (click)="editRequest()"
            title="Open the full form to view / edit">
          </button>
          <span class="status-pill status-pill--{{ statusSeverity(selectedReq.status) }}">
            {{ selectedReq.status }}
          </span>
        </div>
      </div>

      <div class="meta-grid">
        <!-- Bulk import batch id; otherwise the entity key (company/employee code). -->
        <div class="meta-item" *ngIf="isBulkImport">
          <span class="meta-label">Import Batch</span>
          <span class="meta-value">{{ selectedReq.transactionId || '—' }}</span>
        </div>
        <!-- Loan screens: show the Employee code (parsed from the transaction id)
             rather than the company code. -->
        <div class="meta-item" *ngIf="!isBulkImport && isLoanScreen">
          <span class="meta-label">Employee</span>
          <span class="meta-value">{{ loanEmployeeDisplay }}</span>
        </div>
        <!-- Separation / Release: show the Employee (code + resolved name). -->
        <div class="meta-item" *ngIf="!isBulkImport && isSeparationScreen">
          <span class="meta-label">Employee</span>
          <span class="meta-value">{{ separationEmployeeDisplay }}</span>
        </div>
        <!-- Payroll Processing: show the Company (code + resolved name). -->
        <div class="meta-item" *ngIf="!isBulkImport && isPayrollScreen">
          <span class="meta-label">Company</span>
          <span class="meta-value">{{ payrollCompanyDisplay }}</span>
        </div>
        <!-- Supplementary / LOP Credit: show the Company (code + resolved name). -->
        <div class="meta-item" *ngIf="!isBulkImport && isSupplementaryScreen">
          <span class="meta-label">Company</span>
          <span class="meta-value">{{ supplementaryCompanyDisplay }}</span>
        </div>
        <div class="meta-item" *ngIf="!isBulkImport && !isLoanScreen && !isSeparationScreen && !isPayrollScreen && !isSupplementaryScreen">
          <span class="meta-label">{{ entity }}</span>
          <span class="meta-value">{{ selectedReq.compCode || selectedReq.transactionId || '—' }}</span>
        </div>
        <div class="meta-item" *ngIf="isBulkImport && getBulkFileName()">
          <span class="meta-label">File Name</span>
          <span class="meta-value">{{ getBulkFileName() }}</span>
        </div>
        <div class="meta-item" *ngIf="isBulkImport && selectedReq.compCode">
          <span class="meta-label">Company</span>
          <span class="meta-value">{{ selectedReq.compCode }}</span>
        </div>
        <div class="meta-item">
          <span class="meta-label">Requested By</span>
          <span class="meta-value">{{ selectedReq.requestedBy }}</span>
        </div>
        <div class="meta-item">
          <span class="meta-label">Requested On</span>
          <span class="meta-value">{{ formatDateTime(selectedReq.requestedOn) }}</span>
        </div>
        <div class="meta-item" *ngIf="isBulkImport">
          <span class="meta-label">{{ bulkCountLabel }}</span>
          <span class="meta-value">{{ getBulkEmployeeCount() }}</span>
        </div>
        <div class="meta-item" *ngIf="isBulkImport && bulkImportInfo?.['importType']">
          <span class="meta-label">Import Type</span>
          <span class="meta-value">{{ bulkImportInfo?.['importType'] === 'N' ? 'New Employees' : 'Amendment' }}</span>
        </div>
        <div class="meta-item">
          <span class="meta-label">Approval Progress</span>
          <span class="meta-value">Level {{ selectedReq.currentLevel }} / {{ selectedReq.totalLevels }}</span>
        </div>
        <div class="meta-item" *ngIf="selectedReq.currentLevelName">
          <span class="meta-label">Current Level</span>
          <span class="meta-value">{{ selectedReq.currentLevelName }}</span>
        </div>
        <div class="meta-item" *ngIf="selectedReq.currentApproverRole">
          <span class="meta-label">Approver Role</span>
          <span class="meta-value">{{ selectedReq.currentApproverRole }}</span>
        </div>
      </div>
    </div>

    <!-- ── Bulk import: Employee Data + original-file download ───────────────
         Mirrors the Employee Approvals "Bulk Import Approval Details" popup. -->
    <div class="section-block-empData" *ngIf="isBulkImport">
      <div class="section-title">
        <i class="pi pi-file-excel"></i>
        <span>Employee Data</span>
      </div>

      <div class="employee-data-info">
        <div class="data-summary">
          <span class="category" *ngIf="getBulkCategory()">Category: {{ getBulkCategory() }}</span>
          <span class="category" *ngIf="bulkImportInfo?.['cCode']">Category: {{ bulkImportInfo?.['cCode'] }}</span>
          <span class="category" *ngIf="bulkImportInfo?.['detailType']">Detail Type: {{ bulkImportInfo?.['detailType'] }}</span>
        </div>

        <!-- Downloadable original file (Employee Master Import only). -->
        <div class="file-download" *ngIf="canDownloadFile">
          <span class="filename">
            <i class="pi pi-file-excel text-success"></i>
            {{ getBulkFileName() }}
          </span>
          <button pButton type="button"
            icon="pi pi-download"
            class="p-button-sm p-button-text p-button-success"
            (click)="downloadExcelFile()"
            title="Download Excel file"
            pTooltip="Download original Excel file">
          </button>
        </div>

        <!-- File name shown informationally when the original file is not stored. -->
        <div class="file-download" *ngIf="!canDownloadFile && getBulkFileName()">
          <span class="filename">
            <i class="pi pi-file-excel text-success"></i>
            {{ getBulkFileName() }}
          </span>
        </div>

        <div class="no-file-info" *ngIf="!getBulkFileName()">
          <i class="pi pi-exclamation-triangle text-warning"></i>
          <span>Excel file not available</span>
        </div>
      </div>
    </div>

    <!-- ── Monthly Input uploaded file (import-originated requests only) ────
         Mirrors the Employee bulk-import popup: show the maker's actual file name
         with a download link. When present it REPLACES the summary below (the
         uploaded sheet is the source of truth). -->
    <div class="section-block" *ngIf="isMonthlyInput && canDownloadFile">
      <div class="section-title">
        <i class="pi pi-file-excel"></i>
        <span>Uploaded File</span>
      </div>
      <div class="employee-data-info">
        <div class="file-download">
          <span class="filename">
            <i class="pi pi-file-excel text-success"></i>
            {{ getBulkFileName() || 'Imported file' }}
          </span>
          <button pButton type="button"
            icon="pi pi-download"
            class="p-button-sm p-button-text p-button-success"
            (click)="downloadExcelFile()"
            title="Download Excel file"
            pTooltip="Download original Excel file">
          </button>
        </div>
      </div>
    </div>

    <!-- ── Monthly Input summary (read-only envelope summary) ───────────────
         Applied BEFORE the generic diff/record path so MONTHLY_INPUT never
         falls through to the field-map diff. Hidden for import-originated requests
         (the Uploaded File section above is the source of truth there). -->
    <div class="section-block" *ngIf="!isBulkImport && isMonthlyInput && !canDownloadFile">
      <div class="section-title">
        <i class="pi pi-table"></i>
        <span>{{ detailsSectionTitle() }}</span>
      </div>

      <div class="diff-wrap">
        <table class="diff-table" *ngIf="monthlyInputSummary.length > 0">
          <thead>
            <tr><th class="dc-label">Field</th><th class="dc-after">Value</th></tr>
          </thead>
          <tbody>
            <tr *ngFor="let row of monthlyInputSummary">
              <td class="dc-label">{{ row.label }}</td>
              <td class="dc-after">{{ row.value || '—' }}</td>
            </tr>
          </tbody>
        </table>
        <div class="diff-empty" *ngIf="monthlyInputSummary.length === 0">
          <i class="pi pi-info-circle"></i> No details available.
        </div>
      </div>
    </div>

    <!-- ── Diff / data table (non-bulk, non-Monthly-Input requests) ──────── -->
    <div class="section-block" *ngIf="!isBulkImport && !isMonthlyInput">
      <div class="section-title">
        <i class="pi pi-table"></i>
        <span>{{ detailsSectionTitle() }}</span>
      </div>

      <div class="diff-wrap">

        <!-- CREATE / DELETE (and Loan Deviation + Separation/Release): single-record
             value view. These are curated single-record views even when the
             transaction is an UPDATE, so no Before→After diff. The Increment screen
             uses the vertical Field/Value layout below instead (it has many fields
             + a multi-line Components value that does not fit one horizontal row). -->
        <table class="record-table"
          *ngIf="!isIncrementScreen && ((selectedReq.transactionType === 'CREATE' || selectedReq.transactionType === 'DELETE') || isLoanDeviation || isSeparationScreen || isPayrollScreen || isSupplementaryScreen) && diffRows.length > 0">
          <thead>
            <tr>
              <th *ngFor="let row of diffRows">{{ row.label }}</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td *ngFor="let row of diffRows">
                <div>{{ ((selectedReq.transactionType === 'DELETE' && !isCuratedRecord) ? row.before : row.after) || '—' }}</div>
                <!-- Optional second line (e.g. loan code under the loan description). -->
                <div class="cell-subline"
                  *ngIf="((selectedReq.transactionType === 'DELETE' && !isCuratedRecord) ? row.subBefore : row.subAfter)">
                  {{ (selectedReq.transactionType === 'DELETE' && !isCuratedRecord) ? row.subBefore : row.subAfter }}
                </div>
              </td>
            </tr>
          </tbody>
        </table>

        <!-- Increment entry: vertical Field / Value layout (one row per field) so
             every field — including the multi-line Components list — is fully
             visible with no horizontal scroll. -->
        <table class="record-table record-table--vertical"
          *ngIf="isIncrementScreen && diffRows.length > 0">
          <tbody>
            <tr *ngFor="let row of diffRows">
              <th scope="row">{{ row.label }}</th>
              <td>
                <div>{{ row.after || '—' }}</div>
                <div class="cell-subline" *ngIf="row.subAfter">{{ row.subAfter }}</div>
              </td>
            </tr>
          </tbody>
        </table>

        <!-- UPDATE: Field / Before / After comparison table (not the curated
             single-record screens, which use the record table above). -->
        <table class="diff-table"
          *ngIf="selectedReq.transactionType === 'UPDATE' && !isLoanDeviation && !isSeparationScreen && !isPayrollScreen && !isSupplementaryScreen && !isIncrementScreen && diffRows.length > 0">
          <thead>
            <tr>
              <th class="dc-label">Field</th>
              <th class="dc-before">Before</th>
              <th class="dc-after">After</th>
            </tr>
          </thead>
          <tbody>
            <tr *ngFor="let row of diffRows" [class.row-changed]="row.changed">
              <td class="dc-label">{{ row.label }}</td>
              <td class="dc-before">
                <span *ngIf="!row.changed">{{ row.before || '—' }}</span>
                <span class="val-before" *ngIf="row.changed">{{ row.before || '—' }}</span>
                <div class="cell-subline" *ngIf="row.subBefore">{{ row.subBefore }}</div>
              </td>
              <td class="dc-after">
                <span *ngIf="!row.changed">{{ row.after || '—' }}</span>
                <span class="val-after" *ngIf="row.changed">{{ row.after || '—' }}</span>
                <div class="cell-subline" *ngIf="row.subAfter">{{ row.subAfter }}</div>
              </td>
            </tr>
          </tbody>
        </table>

        <div class="diff-empty" *ngIf="diffRows.length === 0">
          <i class="pi pi-info-circle"></i> No field data available.
        </div>
      </div>
    </div>

    <!-- ── History timeline ──────────────────────────────────────────────── -->
    <div class="section-block" *ngIf="(selectedReq.history?.length || 0) > 0">
      <div class="section-title">
        <i class="pi pi-history"></i>
        <span>Approval History</span>
      </div>
      <div class="timeline-scroll">
        <div class="timeline">
          <div class="tl-item" *ngFor="let action of selectedReq.history">
            <div class="tl-dot tl-dot--{{ action.action.toLowerCase() }}"></div>
            <div class="tl-body">
              <div class="tl-top">
                <span class="tl-badge tl-badge--{{ action.action.toLowerCase() }}">{{ action.action }}</span>
                <span class="tl-level" *ngIf="action.levelNo > 0">Level {{ action.levelNo }}</span>
                <span class="tl-date">{{ formatDateTime(action.actionOn) }}</span>
              </div>
              <div class="tl-by">{{ action.actionBy }}</div>
              <div class="tl-comment" *ngIf="action.comments">
                <i class="pi pi-comment"></i> {{ action.comments }}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- ── Action buttons (approver only, while pending) ─────────────────── -->
    <div class="action-bar" *ngIf="canAct">
      <button pButton type="button" icon="pi pi-check" label="Approve"
        class="p-button-success p-button-sm"
        [disabled]="actioning"
        (click)="openAction('approve')">
      </button>
      <button pButton type="button" icon="pi pi-reply" label="Return to Maker"
        class="p-button-warning p-button-sm"
        [disabled]="actioning"
        (click)="openAction('return')">
      </button>
      <button pButton type="button" icon="pi pi-times" label="Reject"
        class="p-button-danger p-button-sm"
        [disabled]="actioning"
        (click)="openAction('reject')">
      </button>
    </div>

  </ng-container>
</p-dialog>

<!-- ══════════════════════════════════════════════════════════════════════════
     ACTION CONFIRMATION DIALOG (approve / return / reject + remarks)
     ═════════════════════════════════════════════════════════════════════════ -->
<p-dialog
  [(visible)]="actionDialogVisible"
  (onHide)="closeAction()"
  [modal]="true"
  [draggable]="false"
  [resizable]="false"
  [dismissableMask]="true"
  [style]="{ width: '460px' }"
  [appendTo]="'body'"
  [header]="actionDialogTitle()"
>
  <div class="action-dlg-body" *ngIf="selectedReq">

    <div class="action-info" *ngIf="actionType === 'approve'">
      <i class="pi pi-check-circle icon-approve"></i>
      <p>
        Approving this request at Level {{ selectedReq.currentLevel }} will
        <ng-container *ngIf="selectedReq.currentLevel >= selectedReq.totalLevels">
          <strong>execute the actual operation</strong> immediately.
        </ng-container>
        <ng-container *ngIf="selectedReq.currentLevel < selectedReq.totalLevels">
          move it to Level {{ selectedReq.currentLevel + 1 }} for further approval.
        </ng-container>
      </p>
    </div>

    <div class="action-info" *ngIf="actionType === 'return'">
      <i class="pi pi-reply icon-return"></i>
      <p>The request will be returned to the maker for correction. The record will remain unchanged.</p>
    </div>

    <div class="action-info" *ngIf="actionType === 'reject'">
      <i class="pi pi-times-circle icon-reject"></i>
      <p>Rejecting will cancel this request. The {{ entity.toLowerCase() }} data will remain unchanged.</p>
    </div>

    <div class="dlg-field">
      <label class="dlg-label">
        Comments
        <span class="req" *ngIf="actionType === 'reject'"> *</span>
      </label>
      <textarea
        pTextarea
        rows="3"
        [(ngModel)]="actionComment"
        [placeholder]="actionType === 'reject' ? 'Rejection reason is required…' : 'Optional comments…'"
        class="dlg-textarea"
        aria-label="Comments"
      ></textarea>
    </div>

  </div>

  <ng-template pTemplate="footer">
    <button pButton type="button" label="Cancel" icon="pi pi-times"
      class="p-button-text p-button-sm"
      (click)="closeAction()"
      [disabled]="actioning">
    </button>
    <button pButton type="button"
      [label]="actionType === 'approve' ? 'Approve' : actionType === 'reject' ? 'Reject' : 'Return'"
      [icon]="actionType === 'approve' ? 'pi pi-check' : actionType === 'reject' ? 'pi pi-times' : 'pi pi-reply'"
      [class]="actionType === 'approve' ? 'p-button-success p-button-sm' : actionType === 'reject' ? 'p-button-danger p-button-sm' : 'p-button-warning p-button-sm'"
      [loading]="actioning"
      (click)="confirmAction()">
    </button>
  </ng-template>
</p-dialog>
